import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Checkbox,
  Card,
  Spin,
  Alert,
  message,
  Tag,
  Tabs,
  Upload,
  Tooltip
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  LinkOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CameraOutlined,
  AuditOutlined,
  StarOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileImageOutlined,
  FileUnknownOutlined,
  UploadOutlined,
  EyeOutlined,
  LoadingOutlined,
  DeleteOutlined,
  CloudUploadOutlined,
  PlusOutlined,
  SafetyCertificateOutlined
} from "@ant-design/icons";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import ButtonBar from "../../components/ButtonNavigation";
import PagesHeader from "../../components/PageHeader";
import { getConsultantProfileData, updateConsultantProfile } from "../../services/ConsultantService";
import { getUserData } from "../../services/GetUserData";
import { set_user_auth } from "../../store/actions/user";
import { MEDIA_UPLOAD_URL, WEBSITE_URL } from "../../Helper";
import "suneditor/dist/css/suneditor.min.css";
import SunEditor from "suneditor-react";
import {
  align,
  font,
  fontColor,
  fontSize,
  formatBlock,
  hiliteColor,
  horizontalRule,
  lineHeight,
  list,
  paragraphStyle,
  table,
  template,
  textStyle,
  link
} from "suneditor/src/plugins";

const { TabPane } = Tabs;

const SunEditorOpts = {
  showPathLabel: false,
  height: "auto",
  minHeight: "180px",
  plugins: [
    align,
    font,
    fontColor,
    fontSize,
    formatBlock,
    hiliteColor,
    horizontalRule,
    lineHeight,
    list,
    paragraphStyle,
    table,
    template,
    textStyle,
    link
  ],
  buttonList: [
    ["undo", "redo"],
    ["font", "fontSize", "formatBlock"],
    ["paragraphStyle"],
    ["bold", "underline", "italic", "strike", "subscript", "superscript"],
    ["fontColor", "hiliteColor"],
    ["removeFormat"],
    ["outdent", "indent"],
    ["align", "horizontalRule", "list", "lineHeight"],
    ["table", "link"],
    ["fullScreen", "showBlocks", "codeView"]
  ]
};

const SunEditorOptsCompact = {
  showPathLabel: false,
  height: "auto",
  minHeight: "140px",
  plugins: [
    align,
    font,
    fontColor,
    fontSize,
    formatBlock,
    hiliteColor,
    horizontalRule,
    lineHeight,
    list,
    paragraphStyle,
    table,
    textStyle,
    link
  ],
  buttonList: [
    ["undo", "redo"],
    ["bold", "underline", "italic"],
    ["fontColor", "hiliteColor"],
    ["align", "list"],
    ["table", "link"],
    ["codeView"]
  ]
};

const SPECIALTY_OPTIONS = [
  "Hardwood Flooring",
  "Carpet & Rugs",
  "Resilient Flooring / LVP",
  "Ceramic Tile & Stone",
  "Laminate Flooring",
  "Moisture Testing & Mitigation",
  "Concrete Subfloors & Prep",
  "Commercial Flooring",
  "Residential Flooring",
  "Failure Analysis & Forensics",
  "Expert Witness & Legal",
  "Insurance & Claims Consulting"
];

const FlooringConsultantDashboard = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useSelector((state) => state.User?.userDetails || state.user?.userData);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);

  // Profile Image State (matches InspectorProfile avatar-wrapper)
  const [imageUrl, setImageUrl] = useState("");
  const [profilePicId, setProfilePicId] = useState(0);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Multi-line Rich Text Editors
  const [consultantBio, setConsultantBio] = useState("");
  const [employmentHistory, setEmploymentHistory] = useState("");
  const [industryTraining, setIndustryTraining] = useState("");
  const [publicationsExperience, setPublicationsExperience] = useState("");
  const [teachingExperience, setTeachingExperience] = useState("");
  const [previousCertifications, setPreviousCertifications] = useState("");
  const [professionalAffiliations, setProfessionalAffiliations] = useState("");

  // Supporting Documents State
  const [supportingDocs, setSupportingDocs] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const consultantIdParam = queryParams.get("consultant_id") || queryParams.get("inspector_id") || 0;

  useEffect(() => {
    dispatch(getUserData())
      .then((userData) => {
        if (userData) {
          dispatch(set_user_auth(userData));
        }
        const queryParams = new URLSearchParams(window.location.search);
        const cIdParam = queryParams.get("consultant_id") || queryParams.get("inspector_id");
        const pId = cIdParam ? parseInt(cIdParam, 10) : (userData?.consultant_profile_id || (userData?.profiles && userData?.profiles[0]?.ID) || 0);
        fetchProfile(pId);
      })
      .catch((err) => {
        console.error("Error fetching user data:", err);
        const queryParams = new URLSearchParams(window.location.search);
        const cIdParam = queryParams.get("consultant_id") || queryParams.get("inspector_id");
        fetchProfile(cIdParam ? parseInt(cIdParam, 10) : 0);
      });
  }, [location.search]);

  const fetchProfile = (targetId = 0) => {
    setLoading(true);
    dispatch(getConsultantProfileData(targetId))
      .then((res) => {
        if (res && res.id) {
          setProfileData(res);
          setImageUrl(res.profile_pic || res.photo_url || "");
          setProfilePicId(res.profile_pic_id || res.thumbnail_id || 0);

          // Rich text states
          setConsultantBio(res.consultant_bio || "");
          setEmploymentHistory(res.employment_history || "");
          setIndustryTraining(res.industry_training || "");
          setPublicationsExperience(res.publications_experience || "");
          setTeachingExperience(res.teaching_experience || "");
          setPreviousCertifications(res.previous_certifications || "");
          setProfessionalAffiliations(res.professional_affiliations || "");

          // Normalize supporting documents
          if (Array.isArray(res.supporting_documents_urls) && res.supporting_documents_urls.length > 0) {
            setSupportingDocs(res.supporting_documents_urls);
          } else if (res.supporting_documents) {
            const rawDocs = Array.isArray(res.supporting_documents)
              ? res.supporting_documents
              : String(res.supporting_documents).split(",").map((s) => s.trim()).filter(Boolean);
            const formatted = rawDocs.map((doc, idx) => {
              const url = typeof doc === "string" ? doc : (doc.url || "");
              const name = doc.name || url.split("/").pop() || `Document #${idx + 1}`;
              return { id: doc.id || 0, url, name };
            });
            setSupportingDocs(formatted);
          } else {
            setSupportingDocs([]);
          }

          form.setFieldsValue({
            organization_name: res.organization_name || res.title || "",
            name: res.name || "",
            phone_number: res.phone_number || "",
            email: res.email || "",
            address: res.address || "",
            website_url: res.website_url || "",
            years_in_industry: res.years_in_industry || "",
            consultant_specialties: res.consultant_specialties || [],
            contractor_licenses: res.contractor_licenses || "",
            current_certifications: res.current_certifications || ""
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching consultant profile:", err);
        message.error("Failed to load consultant profile data.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Profile Image Avatar Upload (matches InspectorProfile logic)
  const handleUploadAvatar = async (options) => {
    const { onSuccess, onError, file } = options;
    setUploadingAvatar(true);
    const fmData = new FormData();
    const config = {
      headers: {
        "content-type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
      },
    };
    fmData.append("file", file);

    try {
      const res = await axios.post(MEDIA_UPLOAD_URL, fmData, config);
      const resp = res.data;
      const uploadedId = typeof resp === "object" && resp !== null ? resp.id : parseInt(resp, 10);
      const uploadedUrl =
        typeof resp === "object" && resp !== null && (resp.source_url || resp.url)
          ? resp.source_url || resp.url
          : URL.createObjectURL(file);

      setImageUrl(uploadedUrl);
      setProfilePicId(uploadedId);
      onSuccess(resp);
      message.success("Profile photo uploaded successfully!");
    } catch (err) {
      console.error("Profile photo upload error:", err);
      onError(err);
      message.error("Failed to upload profile photo.");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const beforeUploadImage = (file) => {
    const isJpgOrPng =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/webp";
    if (!isJpgOrPng) {
      message.error("Please upload a JPG, PNG, or WEBP image file.");
      return false;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image file size must be less than 5MB.");
      return false;
    }
    return true;
  };

  const ProfileuploadButton = (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
      <PlusOutlined style={{ fontSize: 20, color: "#334155", marginBottom: 6 }} />
      <span style={{ fontSize: 13, fontWeight: 500, color: "#334155" }}>
        Upload Profile
      </span>
    </div>
  );

  // Supporting Document Upload
  const handleUploadDocument = async (options) => {
    const { onSuccess, onError, file } = options;
    setUploadingDoc(true);
    const fmData = new FormData();
    const config = {
      headers: {
        "content-type": "multipart/form-data",
        Authorization: `Bearer ${localStorage.getItem("jwt_token")}`,
      },
    };
    fmData.append("file", file);

    try {
      const res = await axios.post(MEDIA_UPLOAD_URL, fmData, config);
      const resp = res.data;
      const uploadedId = typeof resp === "object" && resp !== null ? resp.id : parseInt(resp, 10);
      const uploadedUrl =
        typeof resp === "object" && resp !== null && (resp.source_url || resp.url)
          ? resp.source_url || resp.url
          : URL.createObjectURL(file);

      const newDoc = {
        id: uploadedId,
        url: uploadedUrl,
        name: file.name || "Supporting Document"
      };

      setSupportingDocs((prev) => [...prev, newDoc]);
      onSuccess(resp);
      message.success(`Uploaded ${file.name}! Click 'Save Profile Changes' to commit.`);
    } catch (err) {
      console.error("Document upload error:", err);
      onError(err);
      message.error("Failed to upload document. Please try again.");
    } finally {
      setUploadingDoc(false);
    }
  };

  const beforeUploadDoc = (file) => {
    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/png"
    ];
    if (!allowed.includes(file.type)) {
      message.error("Allowed document types: PDF, Word (DOC/DOCX), JPG, or PNG.");
      return false;
    }
    const isLt20M = file.size / 1024 / 1024 < 20;
    if (!isLt20M) {
      message.error("File size must be under 20MB.");
      return false;
    }
    return true;
  };

  const handleDeleteDocument = (idxToRemove) => {
    setSupportingDocs((prev) => prev.filter((_, idx) => idx !== idxToRemove));
    message.info("Document removed from list. Click 'Save Profile Changes' to commit.");
  };

  const getDocIcon = (filename = "") => {
    const lower = filename.toLowerCase();
    if (lower.endsWith(".pdf")) {
      return <FilePdfOutlined style={{ fontSize: 24, color: "#ef4444" }} />;
    }
    if (lower.endsWith(".doc") || lower.endsWith(".docx")) {
      return <FileWordOutlined style={{ fontSize: 24, color: "#2563eb" }} />;
    }
    if (lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".png") || lower.endsWith(".webp")) {
      return <FileImageOutlined style={{ fontSize: 24, color: "#10b981" }} />;
    }
    return <FileUnknownOutlined style={{ fontSize: 24, color: "#64748b" }} />;
  };

  const onFinish = (values) => {
    setSaving(true);
    const docUrls = supportingDocs.map((d) => d.url || d.id).filter(Boolean);

    const payload = {
      ...values,
      profile_id: profileData?.id || (consultantIdParam ? parseInt(consultantIdParam, 10) : 0),
      profile_pic_id: profilePicId || 0,
      thumbnail_id: profilePicId || 0,
      consultant_bio: consultantBio || "",
      employment_history: employmentHistory || "",
      industry_training: industryTraining || "",
      publications_experience: publicationsExperience || "",
      teaching_experience: teachingExperience || "",
      previous_certifications: previousCertifications || "",
      professional_affiliations: professionalAffiliations || "",
      supporting_documents: docUrls.length > 0 ? docUrls.join(",") : ""
    };

    dispatch(updateConsultantProfile(payload))
      .then((res) => {
        message.success("Flooring Consultant profile updated successfully!");
        if (res && res.id) {
          setProfileData(res);
          if (res.profile_pic || res.photo_url) {
            setImageUrl(res.profile_pic || res.photo_url);
          }
          if (res.profile_pic_id || res.thumbnail_id) {
            setProfilePicId(res.profile_pic_id || res.thumbnail_id);
          }
        }
        dispatch(getUserData()).then((userData) => {
          if (userData) {
            dispatch(set_user_auth(userData));
          }
        }).catch(() => {});
      })
      .catch((err) => {
        console.error("Error saving profile:", err);
        message.error("Failed to update profile. Please try again.");
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const websiteUrl = (WEBSITE_URL || process.env.REACT_APP_WEBSITE_URL || "https://www.floordetective.com").replace(/\/+$/, "");
  const checkoutUrl = `${websiteUrl}/membership-account/membership-levels/`;

  if (loading) {
    return (
      <div className="inspectionWrapper" style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" tip="Loading consultant profile..." />
      </div>
    );
  }

  const verStatus = profileData?.verification_status || currentUser?.verification_status || "pending_review";
  const isApproved = verStatus === "approved" || verStatus === "verified";
  const isPaid = !!profileData?.is_active_paid || !!profileData?.is_premium || !!currentUser?.is_active_paid || !!currentUser?.is_premium;
  const isPhotoTier = !!profileData?.has_photo_tier || !!currentUser?.has_photo_tier || String(profileData?.membership_id) === '28' || String(currentUser?.membership_id) === '28';
  const permalink = profileData?.permalink || "";

  return (
    <div className="inspectionWrapper trusted-dealer-dashboard-wrapper">
      <div className="inspectionHeader">
        <PagesHeader title="Flooring Consultant Profile" />
      </div>

      <div className="buttonSection" style={{ position: "relative", zIndex: 100 }}>
        <div className="container-xxl">
          <ButtonBar isPaid={isPaid} />
        </div>
      </div>

      <div className="container-xxl inspector-profile-container" style={{ padding: "20px 0" }}>
        <Row justify="center">
          <Col xs={24} md={20}>
            {/* Dynamic Status Banners */}
            {!isApproved && (
              <Alert
                message="⏳ Application Pending Verification"
                description="Your Flooring Consultant credentials and uploaded documents are currently under review by our administrative board. Once verified, your listing will be active on the nationwide consultant map."
                type="warning"
                showIcon
                icon={<ClockCircleOutlined style={{ fontSize: 24 }} />}
                style={{ borderRadius: 10, marginBottom: 20, padding: 16 }}
              />
            )}

            {!isPaid && (
              <Alert
                message="⚠️ Membership Checkout Required"
                description={
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginTop: 4 }}>
                    <span style={{ fontSize: 13.5, color: "#7f1d1d" }}>
                      To activate your public listing and consultant directory benefits, please complete checkout for your Flooring Consultant annual plan.
                    </span>
                    <Button
                      type="primary"
                      danger
                      href={checkoutUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ borderRadius: 6, fontWeight: 700, height: 38 }}
                    >
                      Complete Plan Checkout &rarr;
                    </Button>
                  </div>
                }
                type="error"
                showIcon
                icon={<ExclamationCircleOutlined style={{ fontSize: 24 }} />}
                style={{ borderRadius: 10, marginBottom: 20, padding: 16, background: "#fff5f5", borderColor: "#fecaca" }}
              />
            )}

            {isApproved && isPaid && (
              <Alert
                message="🟢 Profile Approved & Live on Floor Detective"
                description={
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginTop: 4 }}>
                    <span>Your Flooring Consultant profile is approved and actively published on the nationwide search directory and map.</span>
                    {permalink && (
                      <Button
                        type="primary"
                        href={permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ background: "#059669", borderColor: "#059669", borderRadius: 6, fontWeight: 700 }}
                      >
                        View Public Profile &rarr;
                      </Button>
                    )}
                  </div>
                }
                type="success"
                showIcon
                icon={<CheckCircleFilled style={{ fontSize: 24 }} />}
                style={{ borderRadius: 10, marginBottom: 20, padding: 16 }}
              />
            )}

            {/* Level 25 Photo App Banner */}
            {isPhotoTier && (
              <Card
                style={{
                  borderRadius: 10,
                  marginBottom: 24,
                  background: "linear-gradient(135deg, #07192f 0%, #0a497b 100%)",
                  color: "#ffffff",
                  border: "1px solid #1e3a5f"
                }}
                bodyStyle={{ padding: "20px 24px" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 10, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>
                      <CameraOutlined style={{ color: "#38bdf8" }} />
                    </div>
                    <div>
                      <h4 style={{ color: "#ffffff", margin: "0 0 4px 0", fontWeight: 800 }}>⭐ Consultant + Photo Plan Active</h4>
                      <p style={{ margin: 0, color: "#cbd5e1", fontSize: "13.5px" }}>You have 1-User seat access to create photo-annotated inspection reports and projects.</p>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10 }}>
                    <Button
                      onClick={() => navigate("/projects")}
                      style={{ background: "#38bdf8", color: "#0f172a", border: "none", fontWeight: 700, borderRadius: 6 }}
                    >
                      Manage Projects
                    </Button>
                    <Button
                      onClick={() => navigate("/photo-reports")}
                      style={{ background: "rgba(255,255,255,0.15)", color: "#ffffff", border: "1px solid rgba(255,255,255,0.3)", fontWeight: 700, borderRadius: 6 }}
                    >
                      Photo Reports
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* EXACT INSPECTOR PROFILE FORM ARCHITECTURE */}
            <Form
              form={form}
              id="editProfileForm"
              name="editProfileForm"
              layout="vertical"
              onFinish={onFinish}
            >
              {/* TOP ROW: AVATAR (LEFT) & STATUS BADGES (RIGHT) */}
              <Row gutter={16} align="middle" justify="space-between">
                <Col xs={24} sm={10} md={8}>
                  <div className="avatar-wrapper">
                    <Spin spinning={uploadingAvatar}>
                      <Upload
                        className="profile-image-upload"
                        customRequest={handleUploadAvatar}
                        showUploadList={false}
                        beforeUpload={beforeUploadImage}
                      >
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt="avatar"
                            className="profileImage-Box"
                            style={{ width: "100%" }}
                          />
                        ) : (
                          ProfileuploadButton
                        )}

                        <Tooltip title="Upload Profile Picture">
                          <div className="upload-button">
                            <CloudUploadOutlined />
                          </div>
                        </Tooltip>
                      </Upload>
                    </Spin>
                  </div>
                </Col>

                {/* STATUS BADGES: PAID / UNPAID & VERIFIED / PENDING */}
                <Col xs={24} sm={14} md={16}>
                  <div style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 12,
                    marginBottom: 20
                  }}>
                    {/* 1. Payment / Membership Plan Badge */}
                    {isPaid ? (
                      <Tag
                        color="success"
                        style={{
                          padding: "6px 16px",
                          borderRadius: 20,
                          fontSize: 13,
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          boxShadow: "0 2px 6px rgba(34, 197, 94, 0.2)",
                          margin: 0
                        }}
                      >
                        <CheckCircleFilled style={{ color: "#16a34a" }} />
                        {profileData?.membership_level ? `${profileData.membership_level}` : "Paid Plan Active"}
                      </Tag>
                    ) : (
                      <Tag
                        color="error"
                        style={{
                          padding: "6px 16px",
                          borderRadius: 20,
                          fontSize: 13,
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          boxShadow: "0 2px 6px rgba(239, 68, 68, 0.2)",
                          margin: 0
                        }}
                      >
                        <ExclamationCircleOutlined style={{ color: "#dc2626" }} />
                        Unpaid / Checkout Required
                      </Tag>
                    )}

                    {/* 2. Verification Status Badge */}
                    {isApproved ? (
                      <Tag
                        color="processing"
                        style={{
                          padding: "6px 16px",
                          borderRadius: 20,
                          fontSize: 13,
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          background: "#f0fdf4",
                          borderColor: "#86efac",
                          color: "#15803d",
                          boxShadow: "0 2px 6px rgba(22, 163, 74, 0.15)",
                          margin: 0
                        }}
                      >
                        <CheckCircleFilled style={{ color: "#16a34a" }} />
                        Verified Consultant
                      </Tag>
                    ) : (
                      <Tag
                        color="warning"
                        style={{
                          padding: "6px 16px",
                          borderRadius: 20,
                          fontSize: 13,
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          background: "#fffbeb",
                          borderColor: "#fde047",
                          color: "#b45309",
                          boxShadow: "0 2px 6px rgba(245, 158, 11, 0.15)",
                          margin: 0
                        }}
                      >
                        <ClockCircleOutlined style={{ color: "#d97706" }} />
                        Pending Verification
                      </Tag>
                    )}
                  </div>
                </Col>
              </Row>

              {/* BASIC DETAILS (3-COLUMN EXACT INSPECTOR PROFILE GRID) */}
              <Row gutter={16}>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item
                    label="Organization / Company Name"
                    name="organization_name"
                    rules={[{ required: true, message: "Please enter organization name!" }]}
                  >
                    <Input
                      prefix={<UserOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Organization Name"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item
                    label="Consultant Full Name"
                    name="name"
                    rules={[{ required: true, message: "Please enter consultant name!" }]}
                  >
                    <Input
                      prefix={<UserOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Consultant Full Name"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item
                    label="Office / Service Address (Map Locator)"
                    name="address"
                    rules={[{ required: true, message: "Please input address!" }]}
                  >
                    <Input
                      prefix={<EnvironmentOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Address"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[{ required: true, type: "email", message: "Please input email!" }]}
                  >
                    <Input
                      prefix={<MailOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Email"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item label="Phone Number" name="phone_number">
                    <Input
                      prefix={<PhoneOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Phone Number"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item label="Website Link (optional)" name="website_url">
                    <Input
                      prefix={<LinkOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="Website URL"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item label="Years in Industry (optional)" name="years_in_industry">
                    <Input
                      prefix={<ClockCircleOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="e.g. 15 Years"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item label="Contractor / Business Licenses (optional)" name="contractor_licenses">
                    <Input
                      prefix={<SafetyCertificateOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="e.g. CA Contractor License #123456"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} xl={8}>
                  <Form.Item
                    label={
                      <span>
                        Current Certifications <Tag color="green" style={{ fontSize: 10, fontWeight: 700, borderRadius: 3, marginLeft: 4 }}>Active Only</Tag>
                      </span>
                    }
                    name="current_certifications"
                    extra={<span style={{ fontSize: 11, color: "#64748b" }}>List only certifications you actively maintain today.</span>}
                  >
                    <Input
                      prefix={<AuditOutlined style={{ color: "#94a3b8" }} />}
                      placeholder="e.g. NWFA, IICRC, FCITS"
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* TABS SECTION (STYLED WITH INSPECTOR'S #editProfileForm PILL BADGES) */}
              <Tabs defaultActiveKey="1" className="profile-tab-section mt-3">
                {/* TAB 1: AREAS OF SPECIALTY */}
                <TabPane tab={<span><StarOutlined /> Areas of Specialty</span>} key="1">
                  <div style={{ padding: "16px 0" }}>
                    <div style={{ marginBottom: 16 }}>
                      <span className="tab-content-title" style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
                        Areas of Specialty & Expertise
                      </span>
                      <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: 13 }}>
                        Select the facets that describe your consulting and inspection practice. These power map filters.
                      </p>
                    </div>

                    <Form.Item name="consultant_specialties" valuePropName="value">
                      <Checkbox.Group style={{ width: "100%" }}>
                        <Row gutter={[16, 16]}>
                          {SPECIALTY_OPTIONS.map((spec) => (
                            <Col xs={24} sm={12} md={8} key={spec}>
                              <Checkbox value={spec} style={{ fontSize: "14px", fontWeight: 500, color: "#1e293b" }}>
                                {spec}
                              </Checkbox>
                            </Col>
                          ))}
                        </Row>
                      </Checkbox.Group>
                    </Form.Item>
                  </div>
                </TabPane>

                {/* TAB 2: UPLOADED REGISTRATION DOCUMENTS */}
                <TabPane tab={<span><AuditOutlined /> Verification Documents</span>} key="2">
                  <div style={{ padding: "16px 0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
                      <div>
                        <span className="tab-content-title" style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
                          Uploaded Registration & Verification Documents
                        </span>
                        <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: 13 }}>
                          Documents submitted during registration for administrative board verification.
                        </p>
                      </div>
                      <Upload
                        showUploadList={false}
                        customRequest={handleUploadDocument}
                        beforeUpload={beforeUploadDoc}
                        disabled={uploadingDoc}
                      >
                        <Button
                          type="primary"
                          icon={uploadingDoc ? <LoadingOutlined /> : <UploadOutlined />}
                          loading={uploadingDoc}
                          style={{ borderRadius: 6, fontWeight: 600, background: "#0a497b", borderColor: "#0a497b" }}
                        >
                          Attach New Document
                        </Button>
                      </Upload>
                    </div>

                    {supportingDocs && supportingDocs.length > 0 ? (
                      <Row gutter={[16, 16]}>
                        {supportingDocs.map((doc, idx) => {
                          const docUrl = doc.url || "";
                          const docName = doc.name || `Supporting Document #${idx + 1}`;
                          return (
                            <Col xs={24} md={12} key={doc.id || idx}>
                              <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "14px 16px",
                                borderRadius: 8,
                                border: "1px solid #e2e8f0",
                                background: "#f8fafc",
                                gap: 12
                              }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                                  <div style={{ flexShrink: 0 }}>
                                    {getDocIcon(docName)}
                                  </div>
                                  <div style={{ minWidth: 0 }}>
                                    <div style={{
                                      fontWeight: 600,
                                      color: "#0f172a",
                                      fontSize: 14,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      maxWidth: 220
                                    }} title={docName}>
                                      {docName}
                                    </div>
                                    <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                                      <Tag color="cyan" style={{ fontSize: 11, borderRadius: 4, margin: 0 }}>
                                        Verification File
                                      </Tag>
                                    </div>
                                  </div>
                                </div>

                                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                                  {docUrl && (
                                    <Button
                                      type="default"
                                      size="small"
                                      icon={<EyeOutlined />}
                                      href={docUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ borderRadius: 4, fontWeight: 600, color: "#0a497b", borderColor: "#0a497b" }}
                                    >
                                      View / Download
                                    </Button>
                                  )}
                                  <Tooltip title="Remove document">
                                    <Button
                                      type="text"
                                      danger
                                      size="small"
                                      icon={<DeleteOutlined />}
                                      onClick={() => handleDeleteDocument(idx)}
                                    />
                                  </Tooltip>
                                </div>
                              </div>
                            </Col>
                          );
                        })}
                      </Row>
                    ) : (
                      <div style={{
                        padding: "24px",
                        textAlign: "center",
                        borderRadius: 8,
                        border: "1px dashed #cbd5e1",
                        background: "#f8fafc"
                      }}>
                        <FileTextOutlined style={{ fontSize: 32, color: "#94a3b8", marginBottom: 8 }} />
                        <div style={{ color: "#64748b", fontWeight: 600, fontSize: 14 }}>
                          No registration documents currently attached.
                        </div>
                        <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 4 }}>
                          You can upload certifications, licenses, or diplomas above to support your verification.
                        </div>
                      </div>
                    )}
                  </div>
                </TabPane>

                {/* TAB 3: EXPERIENCE & CREDENTIALS (SUNEDITORS) */}
                <TabPane tab={<span><FileTextOutlined /> Experience & Credentials</span>} key="3">
                  <div style={{ padding: "16px 0" }}>
                    <Row gutter={16}>
                      <Col xs={24} md={12} style={{ marginBottom: 20 }}>
                        <Form.Item label={<strong>Employment History (optional)</strong>}>
                          <div className="suneditor-consultant-wrapper" style={{ border: "1px solid #cbd5e1", borderRadius: 8, overflow: "hidden" }}>
                            <SunEditor
                              setContents={employmentHistory}
                              onChange={(content) => setEmploymentHistory(content)}
                              setOptions={SunEditorOptsCompact}
                              placeholder="Relevant company roles, consulting history, advisory positions..."
                            />
                          </div>
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12} style={{ marginBottom: 20 }}>
                        <Form.Item label={<strong>Industry Training & Education (optional)</strong>}>
                          <div className="suneditor-consultant-wrapper" style={{ border: "1px solid #cbd5e1", borderRadius: 8, overflow: "hidden" }}>
                            <SunEditor
                              setContents={industryTraining}
                              onChange={(content) => setIndustryTraining(content)}
                              setOptions={SunEditorOptsCompact}
                              placeholder="Manufacturer academies, hands-on training, continuing education..."
                            />
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col xs={24} md={12} style={{ marginBottom: 20 }}>
                        <Form.Item label={<strong>Professional Affiliations (optional)</strong>}>
                          <div className="suneditor-consultant-wrapper" style={{ border: "1px solid #cbd5e1", borderRadius: 8, overflow: "hidden" }}>
                            <SunEditor
                              setContents={professionalAffiliations}
                              onChange={(content) => setProfessionalAffiliations(content)}
                              setOptions={SunEditorOptsCompact}
                              placeholder="e.g. WFCA, NWFA, CFI, FCITS affiliations..."
                            />
                          </div>
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12} style={{ marginBottom: 20 }}>
                        <Form.Item label={<strong>Publications & Technical Papers (optional)</strong>}>
                          <div className="suneditor-consultant-wrapper" style={{ border: "1px solid #cbd5e1", borderRadius: 8, overflow: "hidden" }}>
                            <SunEditor
                              setContents={publicationsExperience}
                              onChange={(content) => setPublicationsExperience(content)}
                              setOptions={SunEditorOptsCompact}
                              placeholder="Articles, industry whitepapers, research published..."
                            />
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col xs={24} md={12} style={{ marginBottom: 20 }}>
                        <Form.Item label={<strong>Teaching & Lecturing Experience (optional)</strong>}>
                          <div className="suneditor-consultant-wrapper" style={{ border: "1px solid #cbd5e1", borderRadius: 8, overflow: "hidden" }}>
                            <SunEditor
                              setContents={teachingExperience}
                              onChange={(content) => setTeachingExperience(content)}
                              setOptions={SunEditorOptsCompact}
                              placeholder="Workshops, trade seminars, installer training..."
                            />
                          </div>
                        </Form.Item>
                      </Col>

                      <Col xs={24} md={12} style={{ marginBottom: 20 }}>
                        <Form.Item
                          label={
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", flexWrap: "wrap", gap: 6 }}>
                              <strong>Previous Certifications (Lapsed / Expired Only)</strong>
                              <Tag color="orange" style={{ margin: 0, fontSize: 11, fontWeight: 700, borderRadius: 4 }}>
                                <ClockCircleOutlined /> Lapsed Credential
                              </Tag>
                            </div>
                          }
                          extra={
                            <span style={{ fontSize: 12, color: "#b45309", marginTop: 4, display: "block", lineHeight: 1.4 }}>
                              List past certifications you previously held that have lapsed or are not currently maintained. They will be publicly labeled with a 'Lapsed' badge and disclaimer on your profile.
                            </span>
                          }
                        >
                          <div className="suneditor-consultant-wrapper" style={{ border: "1px solid #cbd5e1", borderRadius: 8, overflow: "hidden" }}>
                            <SunEditor
                              setContents={previousCertifications}
                              onChange={(content) => setPreviousCertifications(content)}
                              setOptions={SunEditorOptsCompact}
                              placeholder="e.g. Past NWFA Certified Hardwood Inspector (2012–2018, Lapsed), Former IICRC Carpet Inspector (Inactive)..."
                            />
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                </TabPane>

                {/* TAB 4: BIOGRAPHY & SUMMARY (SUNEDITOR) */}
                <TabPane tab={<span><FileTextOutlined /> Biography & Summary</span>} key="4">
                  <div style={{ padding: "16px 0" }}>
                    <div style={{ marginBottom: 12 }}>
                      <span className="tab-content-title" style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
                        Professional Biography & Public Summary
                      </span>
                      <p style={{ color: "#64748b", fontSize: 13, margin: "4px 0 12px" }}>
                        This rich text overview is showcased on your public Floor Detective profile card and single page.
                      </p>
                    </div>

                    <div className="suneditor-consultant-wrapper" style={{ border: "1px solid #cbd5e1", borderRadius: 8, overflow: "hidden" }}>
                      <SunEditor
                        setContents={consultantBio}
                        onChange={(content) => setConsultantBio(content)}
                        setOptions={SunEditorOpts}
                        placeholder="Introduce your background, technical consulting approach, forensic inspection services, courtroom/expert witness history, and client commitment..."
                      />
                    </div>
                  </div>
                </TabPane>
              </Tabs>

              {/* EXACT INSPECTOR PROFILE BOTTOM BUTTON DESIGN */}
              <Form.Item className="buttonOuter mt-4 text-center">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  style={{ height: 44, padding: "0 45px", fontSize: 15, fontWeight: 700 }}
                >
                  Save Profile Changes
                </Button>
                {permalink && isApproved && isPaid && (
                  <Button
                    type="default"
                    href={permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginLeft: 16, height: 44, padding: "0 25px", fontSize: 14, fontWeight: 600, borderRadius: 50 }}
                  >
                    View Public Profile
                  </Button>
                )}
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default FlooringConsultantDashboard;
